import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Clipboard, ArrowLeft, ExternalLink } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import ProfileIconLink from "@/components/ProfileIconLink";
import { TypeformWidget } from "@/components/assessments/TypeformWidget";
import { getClientAssessment, type ClientAssessment } from "@/integrations/backend/assessments";
import { normalizeTypeformUrlOrEmpty } from "@/lib/typeform";
import { ClientPageLayout } from "@/components/client/ClientPageLayout";

export default function AssessmentEmbedPage() {
  const { assessmentId } = useParams<{ assessmentId: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [assessment, setAssessment] = useState<ClientAssessment | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const typeformLink = React.useMemo(() => {
    const raw = assessment?.typeform_url ?? "";
    const res = normalizeTypeformUrlOrEmpty(raw);
    return res.ok ? res.url : raw;
  }, [assessment?.typeform_url]);

  useEffect(() => {
    let active = true;
    (async () => {
      if (!assessmentId) {
        navigate("/explore?tab=assessments", { replace: true });
        return;
      }
      try {
        setLoading(true);
        setError(null);
        const row = await getClientAssessment(assessmentId);
        if (!active) return;
        setAssessment(row);
      } catch (e) {
        if (!active) return;
        setError(e instanceof Error ? e.message : "Failed to load assessment");
        setAssessment(null);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [assessmentId, navigate]);

  const goBack = () => navigate("/explore?tab=assessments");

  const onSubmit = () => {
    toast({
      title: "Assessment submitted",
      description: "Thanks — your submission was received.",
    });
    navigate("/explore?tab=assessments");
  };

  return (
    <ClientPageLayout
      activeTab="explore"
      header={
        <>
          <div className="flex items-center justify-between mb-3">
            <Button
              variant="ghost"
              className="bg-white/10 rounded-full h-9 px-3 flex items-center justify-center hover:bg-white/20"
              onClick={goBack}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              <span className="text-sm">Back to Explore</span>
            </Button>
            <ProfileIconLink />
          </div>

          <div className="flex items-center gap-2">
            <Clipboard className="w-6 h-6 text-primary shrink-0" />
            <h1 className="text-2xl font-bold">{assessment?.name ?? "Assessment"}</h1>
          </div>
          {assessment?.description ? (
            <p className="text-gray-light text-sm mt-2">{assessment.description}</p>
          ) : null}
        </>
      }
    >
      {error ? (
        <Card className="border border-primary/20 shadow-card bg-dark-card">
          <CardContent className="p-4 text-sm text-red-200">Failed to load assessment: {error}</CardContent>
        </Card>
      ) : null}

      {loading ? (
        <Card className="border border-primary/20 shadow-card bg-dark-card">
          <CardContent className="p-8 text-center text-white">Loading…</CardContent>
        </Card>
      ) : !assessment ? (
        <Card className="border border-primary/20 shadow-card bg-dark-card">
          <CardContent className="p-8 text-center text-white">
            <div className="font-semibold mb-2">Assessment not found</div>
            <Button variant="outline" className="border-primary/30 text-primary hover:bg-primary hover:text-black" onClick={goBack}>
              Go back
            </Button>
          </CardContent>
        </Card>
      ) : assessment.typeform_url ? (
        <>
          <TypeformWidget typeformUrl={assessment.typeform_url} onSubmit={onSubmit} />
          <div className="flex justify-end">
            <Button asChild variant="outline" className="border-primary/30 text-primary hover:bg-primary hover:text-black">
              <a href={typeformLink} target="_blank" rel="noreferrer">
                <ExternalLink className="w-4 h-4 mr-2" />
                Open in new tab
              </a>
            </Button>
          </div>
        </>
      ) : (
        <Card className="border border-primary/20 shadow-card bg-dark-card">
          <CardContent className="p-8 text-center text-white">
            <div className="font-semibold mb-2">Coming soon</div>
            <div className="text-sm text-gray-light mb-4">This assessment doesn’t have a Typeform link yet.</div>
            <Button variant="outline" className="border-primary/30 text-primary hover:bg-primary hover:text-black" onClick={goBack}>
              Go back
            </Button>
          </CardContent>
        </Card>
      )}
    </ClientPageLayout>
  );
}
